import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import {
  Shield, AlertTriangle, Sparkles, ChevronRight, ChevronLeft, Info, BookOpen,
  Compass, Heart, Users, Activity, Loader2, Printer, RefreshCcw, CheckCircle2,
  Lock, Eye, HandHeart, MessageSquare,
} from "lucide-react";

/* --------------------------------------------------------------------------
   SCAN AP — Evaluatorul Tiparelor de Alienare (frontend)
   Public. Multi-stage: intro -> consent -> setup -> questionnaire -> results.
   NU este un instrument de diagnostic. Prioritizează siguranța copilului.
-------------------------------------------------------------------------- */

const AGE_RANGES = [
  { v: "3-5", label: "3-5 ani" },
  { v: "6-8", label: "6-8 ani" },
  { v: "9-11", label: "9-11 ani" },
  { v: "12-14", label: "12-14 ani" },
  { v: "15-17", label: "15-17 ani" },
];

const PERSPECTIVES = [
  { v: "mama", label: "Mamă" },
  { v: "tata", label: "Tată" },
  { v: "bunic", label: "Bunic/Bunică" },
  { v: "tutore", label: "Tutore" },
  { v: "profesionist", label: "Profesionist (psiholog / asistent social)" },
  { v: "altul", label: "Alt adult apropiat" },
];

const CONSENT_ITEMS = [
  {
    code: "not_diagnosis",
    label: "Înțeleg că SCAN AP NU este un instrument de diagnostic medical, psihologic sau juridic.",
  },
  {
    code: "not_guilt",
    label: "Înțeleg că rezultatele NU identifică un vinovat și NU dovedesc intenția vreunui părinte.",
  },
  {
    code: "based_on_observations",
    label: "Voi răspunde pe baza unor observații concrete, nu pe interpretări sau relatări indirecte.",
  },
  {
    code: "safety_priority",
    label: "Înțeleg că siguranța copilului are ÎNTOTDEAUNA prioritate — dacă apar semnale, voi cere ajutor profesionist.",
  },
  {
    code: "gdpr_read",
    label: "Sunt de acord cu prelucrarea anonimă a răspunsurilor pentru generarea raportului (GDPR).",
  },
];

const FREQ_OPTIONS = [
  { v: "nu_am_observat", label: "Nu am observat" },
  { v: "rar", label: "Rar" },
  { v: "uneori", label: "Uneori" },
  { v: "des", label: "Des" },
  { v: "foarte_des", label: "Foarte des" },
  { v: "nu_stiu", label: "Nu știu" },
  { v: "prefer_sa_nu", label: "Prefer să nu răspund" },
];

const SOURCE_OPTIONS = [
  { v: "direct", label: "Am văzut/auzit direct" },
  { v: "copil", label: "Mi-a spus copilul" },
  { v: "document", label: "Există dovadă (mesaj, înregistrare, document)" },
  { v: "tert", label: "Mi-a spus o terță persoană" },
  { v: "interpretare", label: "Este interpretarea mea" },
];

const PERIOD_OPTIONS = [
  { v: "luna", label: "În ultima lună" },
  { v: "3_luni", label: "Ultimele 3 luni" },
  { v: "6_luni", label: "Ultimele 6 luni" },
  { v: "peste_6_luni", label: "Peste 6 luni" },
];

const SECTION_META = {
  child_language: { label: "Ce spune copilul", icon: MessageSquare, color: "#D4A63D" },
  contact_transitions: { label: "Înainte, în timpul, după contact", icon: Compass, color: "#5FA0E8" },
  child_state: { label: "Comportament și stare", icon: Heart, color: "#E89BB8" },
  time_changes: { label: "Schimbări în timp", icon: Activity, color: "#9C8FE0" },
  adult_behaviors: { label: "Comportamentele adulților", icon: Users, color: "#6C7A9C" },
  safety: { label: "Siguranță", icon: Shield, color: "#C83A3A" },
};

/* ============ INTRO ============ */
const Intro = ({ onStart }) => (
  <section data-testid="scanap-intro" className="relative pt-32 pb-16 md:pt-40 md:pb-24">
    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0A1020] via-[#0F1830] to-[#0A1020]" />
    <div className="max-w-5xl mx-auto px-6">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D4A63D]/30 bg-[#D4A63D]/5 text-[#D4A63D] text-[11px] tracking-widest uppercase mb-6">
        <Sparkles className="w-3 h-3" /> Evaluator Comportamental
      </div>
      <h1 className="font-[Playfair_Display] text-5xl md:text-7xl leading-[1.05] text-white">
        SCAN <span className="text-[#D4A63D]">AP</span>
      </h1>
      <p className="mt-3 text-[#A0AABF] text-lg md:text-xl max-w-3xl">
        Evaluatorul Tiparelor de Alienare. Un instrument de <span className="text-white">orientare</span> pentru părinți care observă schimbări îngrijorătoare în relația cu propriul copil.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="p-6 rounded-md border border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md bg-[#D4A63D]/10 grid place-items-center">
              <Compass className="w-5 h-5 text-[#D4A63D]" />
            </div>
            <h3 className="text-white font-semibold tracking-wide">Ce evaluează</h3>
          </div>
          <p className="text-sm text-[#A0AABF] leading-relaxed">
            5 dimensiuni: Influențare negativă, Siguranță (prioritară), Preferință și adaptare, Atașament și anxietate, Conflict parental.
          </p>
        </div>
        <div className="p-6 rounded-md border border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-md bg-[#C83A3A]/10 grid place-items-center">
              <Shield className="w-5 h-5 text-[#C83A3A]" />
            </div>
            <h3 className="text-white font-semibold tracking-wide">Ce NU face</h3>
          </div>
          <p className="text-sm text-[#A0AABF] leading-relaxed">
            NU diagnostichează. NU identifică vinovați. NU înlocuiește evaluarea unui psiholog. Prioritizează întotdeauna siguranța copilului.
          </p>
        </div>
      </div>

      <div className="mt-10 p-5 rounded-md border border-[#C83A3A]/30 bg-[#C83A3A]/5 flex gap-4">
        <AlertTriangle className="w-6 h-6 text-[#C83A3A] shrink-0 mt-0.5" />
        <div className="text-sm text-[#F8F8F4]/90 leading-relaxed">
          <p>
            <b className="text-[#C83A3A]">Siguranța copilului are prioritate absolută.</b> Dacă există pericol imediat — violență, abuz, amenințări sau gânduri de auto-vătămare — sună <b>112</b> pentru urgențe și cere ajutor real. Nu folosi acest instrument ca substitut pentru intervenție.
            Pentru sprijin și consiliere, apelează <b>0800 801 200</b> (Telefonul Copilului) sau <b className="text-[#D4A63D]">119</b> — linia națională unică, gratuită și confidențială pentru semnalarea abuzului, neglijării, exploatării sau oricărei forme de violență asupra copiilor. Disponibilă <b>24/7</b>, poate fi apelată deopotrivă de copii, părinți sau adulți apropiați.
          </p>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <button
          data-testid="scanap-start-btn"
          onClick={onStart}
          className="btn-gold px-8 py-3.5 rounded-sm text-sm font-bold tracking-widest inline-flex items-center gap-2"
        >
          ÎNCEPE EVALUAREA <ChevronRight className="w-4 h-4" />
        </button>
        <Link
          to="/ajutor"
          data-testid="scanap-help-link"
          className="text-[#A0AABF] hover:text-[#D4A63D] text-sm tracking-wider inline-flex items-center gap-2"
        >
          <BookOpen className="w-4 h-4" /> Ghid de ajutor
        </Link>
      </div>

      <p className="mt-8 text-xs text-[#6C7A9C] leading-relaxed max-w-3xl">
        Instrumentul funcționează pe baza unui algoritm publicat (v1.0.0) și oferă o hartă vizuală a observațiilor tale. Rezultatele sunt confidențiale și anonime. Le poți salva doar dacă alegi să te autentifici. Timp estimat: 8-15 minute (mod complet), 4-6 minute (mod rapid).
      </p>
    </div>
  </section>
);

/* ============ CONSENT MODAL / STEP ============ */
const ConsentStep = ({ onBack, onAccept }) => {
  const [checked, setChecked] = useState({});
  const allChecked = CONSENT_ITEMS.every((c) => checked[c.code]);

  return (
    <section data-testid="scanap-consent" className="max-w-3xl mx-auto px-6 py-12">
      <div className="mb-6 flex items-center gap-2 text-[#A0AABF] text-xs tracking-widest uppercase">
        <Lock className="w-4 h-4" /> Pasul 1 din 3 · Acord & Confidențialitate
      </div>
      <h2 className="font-[Playfair_Display] text-3xl md:text-5xl text-white leading-tight mb-6">
        Înainte să începi, confirmă că <span className="text-[#D4A63D]">înțelegi limitele</span>.
      </h2>
      <div className="space-y-3">
        {CONSENT_ITEMS.map((c) => (
          <label
            key={c.code}
            data-testid={`scanap-consent-${c.code}`}
            className="flex items-start gap-3 p-4 rounded-md border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] cursor-pointer transition"
          >
            <input
              type="checkbox"
              checked={!!checked[c.code]}
              onChange={(e) => setChecked((s) => ({ ...s, [c.code]: e.target.checked }))}
              className="mt-1 w-4 h-4 accent-[#D4A63D]"
            />
            <span className="text-[#F8F8F4]/90 text-sm leading-relaxed">{c.label}</span>
          </label>
        ))}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button
          data-testid="scanap-consent-back"
          onClick={onBack}
          className="text-[#A0AABF] hover:text-white text-sm inline-flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" /> Înapoi
        </button>
        <button
          data-testid="scanap-consent-continue"
          disabled={!allChecked}
          onClick={() => onAccept(CONSENT_ITEMS.map((c) => c.code))}
          className={`px-8 py-3 rounded-sm text-sm font-bold tracking-widest inline-flex items-center gap-2 transition ${
            allChecked ? "btn-gold" : "bg-white/5 text-white/30 cursor-not-allowed"
          }`}
        >
          CONTINUĂ <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
};

/* ============ SETUP STEP ============ */
const SetupStep = ({ consent, onBack, onStart, loading }) => {
  const [pseudonym, setPseudonym] = useState("");
  const [ageRange, setAgeRange] = useState("6-8");
  const [perspective, setPerspective] = useState("tata");
  const [mode, setMode] = useState("complet");

  return (
    <section data-testid="scanap-setup" className="max-w-3xl mx-auto px-6 py-12">
      <div className="mb-6 flex items-center gap-2 text-[#A0AABF] text-xs tracking-widest uppercase">
        <Eye className="w-4 h-4" /> Pasul 2 din 3 · Context
      </div>
      <h2 className="font-[Playfair_Display] text-3xl md:text-5xl text-white leading-tight mb-6">
        Câteva detalii <span className="text-[#D4A63D]">anonime</span>.
      </h2>
      <p className="text-[#A0AABF] mb-8">
        Aceste informații ne ajută să calibrăm raportul. Nu vor fi asociate cu identitatea reală a copilului.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-white text-sm font-semibold mb-2">Pseudonim pentru copil</label>
          <input
            data-testid="scanap-input-pseudonym"
            value={pseudonym}
            onChange={(e) => setPseudonym(e.target.value.slice(0, 60))}
            placeholder="ex: Micuțul, Ana-M, Copilul meu (fără nume real)"
            className="w-full px-4 py-3 rounded-sm bg-[#0F1830] border border-white/10 focus:border-[#D4A63D] outline-none text-white"
          />
        </div>

        <div>
          <label className="block text-white text-sm font-semibold mb-2">Grupă de vârstă</label>
          <div className="grid grid-cols-5 gap-2">
            {AGE_RANGES.map((a) => (
              <button
                key={a.v}
                data-testid={`scanap-age-${a.v}`}
                onClick={() => setAgeRange(a.v)}
                className={`px-3 py-2.5 rounded-sm text-xs font-semibold border transition ${
                  ageRange === a.v
                    ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]"
                    : "bg-white/[0.02] border-white/10 text-[#A0AABF] hover:border-[#D4A63D]/40"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-white text-sm font-semibold mb-2">Din ce perspectivă răspunzi?</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PERSPECTIVES.map((p) => (
              <button
                key={p.v}
                data-testid={`scanap-perspective-${p.v}`}
                onClick={() => setPerspective(p.v)}
                className={`px-3 py-3 rounded-sm text-xs font-semibold border transition text-left ${
                  perspective === p.v
                    ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]"
                    : "bg-white/[0.02] border-white/10 text-[#F8F8F4]/85 hover:border-[#D4A63D]/40"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-white text-sm font-semibold mb-2">Mod de evaluare</label>
          <div className="grid md:grid-cols-2 gap-3">
            <button
              data-testid="scanap-mode-rapid"
              onClick={() => setMode("rapid")}
              className={`p-4 rounded-sm border text-left transition ${
                mode === "rapid" ? "border-[#D4A63D] bg-[#D4A63D]/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
            >
              <div className="text-white font-semibold mb-1">Rapid (4-6 min)</div>
              <div className="text-xs text-[#A0AABF]">Set esențial + toate întrebările de siguranță.</div>
            </button>
            <button
              data-testid="scanap-mode-complet"
              onClick={() => setMode("complet")}
              className={`p-4 rounded-sm border text-left transition ${
                mode === "complet" ? "border-[#D4A63D] bg-[#D4A63D]/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"
              }`}
            >
              <div className="text-white font-semibold mb-1">Complet (8-15 min)</div>
              <div className="text-xs text-[#A0AABF]">Toate 39 de întrebări pentru raport nuanțat.</div>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-between">
        <button
          data-testid="scanap-setup-back"
          onClick={onBack}
          className="text-[#A0AABF] hover:text-white text-sm inline-flex items-center gap-1"
        >
          <ChevronLeft className="w-4 h-4" /> Înapoi
        </button>
        <button
          data-testid="scanap-setup-continue"
          disabled={loading}
          onClick={() =>
            onStart({
              mode,
              consent_flags: consent,
              child_pseudonym: pseudonym.trim() || "Copil",
              child_age_range: ageRange,
              perspective,
            })
          }
          className="btn-gold px-8 py-3 rounded-sm text-sm font-bold tracking-widest inline-flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          ÎNCEPE CHESTIONARUL
        </button>
      </div>
    </section>
  );
};

/* ============ QUESTIONNAIRE ============ */
const Questionnaire = ({ questions, onSubmit, submitting, setup }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const q = questions[idx];
  const total = questions.length;
  const answered = Object.keys(answers).length;
  const progress = Math.round(((idx + 1) / total) * 100);
  const meta = SECTION_META[q?.section] || {};
  const Icon = meta.icon || Info;

  const updateAnswer = (patch) => {
    setAnswers((s) => ({
      ...s,
      [q.code]: {
        question_code: q.code,
        frequency: "uneori",
        source: "direct",
        period: "3_luni",
        ...(s[q.code] || {}),
        ...patch,
      },
    }));
  };
  const skip = () => {
    updateAnswer({ frequency: "nu_am_observat" });
    goNext();
  };
  const goNext = () => {
    if (idx < total - 1) setIdx(idx + 1);
    else finish();
  };
  const finish = () => {
    // Ensure all questions have at least a "nu_am_observat" entry
    const list = questions.map((qq) => answers[qq.code] || {
      question_code: qq.code, frequency: "nu_am_observat", source: "direct", period: "3_luni",
    });
    onSubmit(list);
  };

  const currentAnswer = answers[q?.code] || { frequency: "uneori", source: "direct", period: "3_luni" };

  if (!q) return null;

  return (
    <section data-testid="scanap-questionnaire" className="max-w-3xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs tracking-widest uppercase text-[#A0AABF] mb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: meta.color }} />
            <span>{meta.label}</span>
          </div>
          <div>Întrebarea {idx + 1} / {total}</div>
        </div>
        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#D4A63D] to-[#E89BB8] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Question */}
      <div className="p-6 md:p-8 rounded-md border border-white/10 bg-white/[0.02]">
        <div className="text-[10px] text-[#6C7A9C] tracking-widest uppercase mb-3">Cod {q.code} · despre {setup.child_pseudonym}</div>
        <h2 className="font-[Playfair_Display] text-2xl md:text-3xl text-white leading-tight">
          {q.title}
        </h2>
        {q.desc && <p className="text-[#A0AABF] mt-3 text-sm leading-relaxed">{q.desc}</p>}
        {q.safety_critical && (
          <div className="mt-4 flex items-start gap-2 text-[#C83A3A] text-xs bg-[#C83A3A]/10 border border-[#C83A3A]/30 rounded-sm p-3">
            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Întrebare de siguranță. Dacă răspunzi afirmativ, îți vom sugera resurse de ajutor imediat.</span>
          </div>
        )}
        {q.protective && (
          <div className="mt-4 flex items-start gap-2 text-[#3BA76D] text-xs bg-[#3BA76D]/10 border border-[#3BA76D]/30 rounded-sm p-3">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Semnal protectiv — răspunsurile pozitive contribuie la echilibrarea raportului.</span>
          </div>
        )}

        {/* Frequency */}
        <div className="mt-6">
          <div className="text-white text-sm font-semibold mb-2">Cât de des observi acest lucru?</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {FREQ_OPTIONS.map((f) => (
              <button
                key={f.v}
                data-testid={`scanap-freq-${f.v}`}
                onClick={() => updateAnswer({ frequency: f.v })}
                className={`px-3 py-2.5 rounded-sm text-xs font-semibold border transition ${
                  currentAnswer.frequency === f.v
                    ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]"
                    : "bg-white/[0.02] border-white/10 text-[#F8F8F4]/85 hover:border-[#D4A63D]/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Source & Period only if freq > 0 */}
        {["rar", "uneori", "des", "foarte_des"].includes(currentAnswer.frequency) && (
          <>
            <div className="mt-5">
              <div className="text-white text-sm font-semibold mb-2">De unde știi?</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {SOURCE_OPTIONS.map((s) => (
                  <button
                    key={s.v}
                    data-testid={`scanap-src-${s.v}`}
                    onClick={() => updateAnswer({ source: s.v })}
                    className={`px-3 py-2 rounded-sm text-xs font-semibold border transition text-left ${
                      currentAnswer.source === s.v
                        ? "bg-[#5FA0E8]/20 text-[#5FA0E8] border-[#5FA0E8]/50"
                        : "bg-white/[0.02] border-white/10 text-[#F8F8F4]/85 hover:border-white/20"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="text-white text-sm font-semibold mb-2">În ce perioadă?</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {PERIOD_OPTIONS.map((p) => (
                  <button
                    key={p.v}
                    data-testid={`scanap-period-${p.v}`}
                    onClick={() => updateAnswer({ period: p.v })}
                    className={`px-3 py-2 rounded-sm text-xs font-semibold border transition ${
                      currentAnswer.period === p.v
                        ? "bg-[#E89BB8]/20 text-[#E89BB8] border-[#E89BB8]/50"
                        : "bg-white/[0.02] border-white/10 text-[#F8F8F4]/85 hover:border-white/20"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Nav */}
      <div className="mt-6 flex items-center justify-between">
        <button
          data-testid="scanap-q-prev"
          disabled={idx === 0}
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          className={`inline-flex items-center gap-1 text-sm ${idx === 0 ? "text-white/20 cursor-not-allowed" : "text-[#A0AABF] hover:text-white"}`}
        >
          <ChevronLeft className="w-4 h-4" /> Anterioara
        </button>

        <div className="flex items-center gap-3">
          <button
            data-testid="scanap-q-skip"
            onClick={skip}
            className="text-xs text-[#6C7A9C] hover:text-white tracking-wider uppercase"
          >
            Sari peste
          </button>
          {idx === total - 1 ? (
            <button
              data-testid="scanap-q-finish"
              disabled={submitting}
              onClick={finish}
              className="btn-gold px-6 py-2.5 rounded-sm text-sm font-bold tracking-widest inline-flex items-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              FINALIZEAZĂ
            </button>
          ) : (
            <button
              data-testid="scanap-q-next"
              onClick={goNext}
              className="btn-gold px-6 py-2.5 rounded-sm text-sm font-bold tracking-widest inline-flex items-center gap-2"
            >
              Următoarea <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-4 text-center text-xs text-[#6C7A9C]">
        Răspuns / total activ: {answered} / {idx + 1}
      </div>
    </section>
  );
};

/* ============ RESULTS ============ */
const InterpretationCard = ({ interp }) => {
  if (!interp) return null;
  const urgent = interp.urgent || interp.code === "SAFETY_PRIORITY";
  return (
    <div
      data-testid="scanap-interpretation"
      className={`p-6 md:p-8 rounded-md border ${urgent ? "border-[#C83A3A]/50 bg-[#C83A3A]/8" : "border-[#D4A63D]/30 bg-[#D4A63D]/5"}`}
    >
      <div className="flex items-start gap-3">
        {urgent ? (
          <AlertTriangle className="w-6 h-6 text-[#C83A3A] shrink-0 mt-0.5" />
        ) : (
          <Sparkles className="w-6 h-6 text-[#D4A63D] shrink-0 mt-0.5" />
        )}
        <div>
          <div className="text-[10px] text-[#A0AABF] tracking-widest uppercase mb-1">Interpretare orientativă</div>
          <h3 className="font-[Playfair_Display] text-2xl md:text-3xl text-white leading-tight">{interp.title}</h3>
          <p className="mt-3 text-[#F8F8F4]/85 leading-relaxed whitespace-pre-line">{interp.text}</p>
        </div>
      </div>
    </div>
  );
};

const Results = ({ result, setup, accessToken, onRestart }) => {
  const [aiSummary, setAiSummary] = useState("");
  const [loadingAi, setLoadingAi] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const chartData = useMemo(
    () => (result.dimensions || []).map((d) => ({
      dimension: d.label.split(" ")[0], // short label
      fullLabel: d.label,
      score: d.score,
      color: d.color,
    })),
    [result]
  );

  const generateAI = async () => {
    setLoadingAi(true);
    try {
      const r = await api.post("/scanap/ai-summary", {
        assessment_id: result.assessment_id,
        access_token: accessToken,
      });
      setAiSummary(r.data?.summary || "");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoadingAi(false);
    }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const url = `${api.defaults.baseURL}/scanap/${result.assessment_id}/pdf?token=${encodeURIComponent(accessToken || "")}`;
      const win = window.open(url, "_blank");
      if (!win) toast.error("Deblochează pop-up-urile pentru a descărca PDF-ul.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Auto-generate AI on mount
  useEffect(() => {
    generateAI();
  }, []);

  const criticalFlags = result.safety_critical_flags || [];

  return (
    <section data-testid="scanap-results" className="max-w-5xl mx-auto px-6 py-12">
      <div className="mb-6 flex items-center gap-2 text-[#A0AABF] text-xs tracking-widest uppercase">
        <CheckCircle2 className="w-4 h-4 text-[#3BA76D]" /> Raport generat · v{result.algorithm_version || "1.0.0"}
      </div>
      <h2 className="font-[Playfair_Display] text-3xl md:text-5xl text-white leading-tight">
        Harta ta pentru <span className="text-[#D4A63D]">{setup.child_pseudonym}</span>
      </h2>
      <p className="text-[#A0AABF] mt-2 max-w-2xl">
        Raportul de mai jos este o hartă vizuală a observațiilor tale. Nu conține diagnostic. Poate ghida următorii pași.
      </p>

      {criticalFlags.length > 0 && (
        <div className="mt-6 p-5 rounded-md border border-[#C83A3A]/50 bg-[#C83A3A]/10 flex gap-4">
          <AlertTriangle className="w-6 h-6 text-[#C83A3A] shrink-0 mt-0.5" />
          <div className="text-sm text-[#F8F8F4]/95 leading-relaxed space-y-2">
            <p>
              <b className="text-[#C83A3A]">Semnale de siguranță detectate ({criticalFlags.length}).</b> Prioritate absolută: verifică siguranța copilului cu un profesionist calificat. Sună <b>112</b> (urgență) sau <b>0800 801 200</b> (Telefonul Copilului) dacă există pericol imediat.
            </p>
            <p className="text-[#F8F8F4]/85">
              <b className="text-[#D4A63D]">119</b> — linia națională gratuită și confidențială pentru semnalarea abuzului, neglijării sau violenței asupra copiilor. Disponibil <b>24/7</b>.
            </p>
          </div>
        </div>
      )}

      {/* Radar + Interpretation */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Radar */}
        <div className="p-6 rounded-md border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] text-[#A0AABF] tracking-widest uppercase mb-2">Radar 5 dimensiuni</div>
          <div className="h-[360px]" data-testid="scanap-radar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={chartData} outerRadius="80%">
                <PolarGrid stroke="#2A3550" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: "#A0AABF", fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#6C7A9C", fontSize: 10 }} stroke="#2A3550" />
                <Radar name="Scor" dataKey="score" stroke="#D4A63D" fill="#D4A63D" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {(result.dimensions || []).map((d) => (
              <div key={d.key} className="flex items-center justify-between text-xs" data-testid={`scanap-dim-${d.key}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                  <span className="text-[#F8F8F4]/90">{d.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold">{d.score}</span>
                  <span className="text-[#6C7A9C]">/ 100</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Interpretation */}
        <div className="space-y-4">
          <InterpretationCard interp={result.primary_interpretation} />
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-sm border border-white/10 bg-white/[0.02] text-center">
              <div className="text-2xl font-bold text-white">{result.answered_count}</div>
              <div className="text-[10px] text-[#A0AABF] tracking-widest uppercase mt-1">răspunsuri</div>
            </div>
            <div className="p-3 rounded-sm border border-white/10 bg-white/[0.02] text-center">
              <div className="text-2xl font-bold text-[#3BA76D]">{result.protective_count}</div>
              <div className="text-[10px] text-[#A0AABF] tracking-widest uppercase mt-1">protective</div>
            </div>
            <div className="p-3 rounded-sm border border-white/10 bg-white/[0.02] text-center">
              <div className="text-2xl font-bold text-[#5FA0E8] capitalize">{result.fundamentation}</div>
              <div className="text-[10px] text-[#A0AABF] tracking-widest uppercase mt-1">fundamentare</div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Summary */}
      <div className="mt-8 p-6 md:p-8 rounded-md border border-[#E89BB8]/30 bg-[#E89BB8]/5" data-testid="scanap-ai-summary">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[#E89BB8]" />
          <h3 className="text-white font-semibold tracking-wide">Rezumat AI — Claude Sonnet</h3>
        </div>
        {loadingAi ? (
          <div className="flex items-center gap-3 text-[#A0AABF] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Generăm un rezumat empatic al observațiilor tale...
          </div>
        ) : aiSummary ? (
          <div className="prose prose-invert prose-sm max-w-none">
            {aiSummary.split(/\n{2,}/).map((p, i) => (
              <p key={i} className="text-[#F8F8F4]/90 leading-relaxed whitespace-pre-line" dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, "<b class='text-white'>$1</b>") }} />
            ))}
          </div>
        ) : (
          <button
            onClick={generateAI}
            data-testid="scanap-ai-retry"
            className="inline-flex items-center gap-2 text-[#E89BB8] hover:text-white text-sm"
          >
            <RefreshCcw className="w-4 h-4" /> Reîncearcă generarea AI
          </button>
        )}
      </div>

      {/* Actions */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="scanap-print-btn"
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="btn-gold px-6 py-3 rounded-sm text-xs font-bold tracking-widest inline-flex items-center gap-2"
          >
            {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
            DESCARCĂ RAPORT PDF
          </button>
          <Link
            to={`/scan-ap/${result.assessment_id}/dashboard?token=${encodeURIComponent(accessToken || "")}`}
            data-testid="scanap-dashboard-cta"
            className="px-6 py-3 rounded-sm text-xs font-bold tracking-widest border border-[#D4A63D]/40 text-[#D4A63D] hover:bg-[#D4A63D]/10 inline-flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" /> JURNAL & CRONOLOGIE
          </Link>
          <Link
            to="/programare"
            data-testid="scanap-book-cta"
            className="px-6 py-3 rounded-sm text-xs font-bold tracking-widest border border-[#E89BB8]/40 text-[#E89BB8] hover:bg-[#E89BB8]/10 inline-flex items-center gap-2"
          >
            <HandHeart className="w-4 h-4" /> VORBEȘTE 1:1 CU GABRIEL
          </Link>
        </div>
        <button
          data-testid="scanap-restart-btn"
          onClick={onRestart}
          className="text-[#A0AABF] hover:text-white text-sm inline-flex items-center gap-2"
        >
          <RefreshCcw className="w-4 h-4" /> Reîncepe o nouă evaluare
        </button>
      </div>

      <div className="mt-10 text-xs text-[#6C7A9C] leading-relaxed">
        Notă legală: SCAN AP nu înlocuiește evaluarea unui psiholog, psihiatru sau avocat autorizat. Rezultatele NU sunt admisibile ca probă științifică într-un dosar juridic. Folosește raportul pentru orientare personală și pentru pregătirea unei consultații profesioniste.
      </div>
    </section>
  );
};

/* ============ ROOT ============ */
export default function ScanAP() {
  const [stage, setStage] = useState("intro"); // intro | consent | setup | questionnaire | results
  const [consent, setConsent] = useState([]);
  const [setup, setSetup] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [assessmentId, setAssessmentId] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const startAssessment = async (setupData) => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        api.get("/scanap/questions", { params: { mode: setupData.mode } }),
        api.post("/scanap/start", setupData),
      ]);
      setQuestions(qRes.data?.questions || []);
      setAssessmentId(sRes.data?.assessment_id);
      setAccessToken(sRes.data?.access_token || null);
      setSetup(setupData);
      setStage("questionnaire");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  const submitAnswers = async (answers) => {
    setSubmitting(true);
    try {
      const r = await api.post("/scanap/submit", { assessment_id: assessmentId, answers });
      setResult(r.data);
      setStage("results");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setConsent([]);
    setSetup(null);
    setQuestions([]);
    setAssessmentId(null);
    setAccessToken(null);
    setResult(null);
    setStage("intro");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0A1020] text-white" data-testid="scanap-page">
      {stage === "intro" && <Intro onStart={() => setStage("consent")} />}
      {stage === "consent" && (
        <ConsentStep
          onBack={() => setStage("intro")}
          onAccept={(flags) => { setConsent(flags); setStage("setup"); }}
        />
      )}
      {stage === "setup" && (
        <SetupStep
          consent={consent}
          onBack={() => setStage("consent")}
          onStart={startAssessment}
          loading={loading}
        />
      )}
      {stage === "questionnaire" && questions.length > 0 && (
        <Questionnaire
          questions={questions}
          onSubmit={submitAnswers}
          submitting={submitting}
          setup={setup}
        />
      )}
      {stage === "results" && result && (
        <Results result={result} setup={setup} accessToken={accessToken} onRestart={restart} />
      )}
    </div>
  );
}
